import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'

import useModal from '@/shared/hooks/useModal'
import { selectAuthData } from '@/containers/Login/AuthSelectors'

import { suppliersManagementActions } from './SuppliersManagementSagas'
import {
  selectSuppliersManagement,
  selectSuppliersManagementCountrySites,
  selectSuppliersManagementCreateStatuses,
} from './SuppliersManagementSelectors'
import {
  updateSuppliersManagementCreateStatuses,
  updateSuppliersManagementFilters,
} from './SuppliersManagementSlice'
import { CountrySite, Supplier, SupplierFilters, SuppliersRoutes } from './types'

interface UseSuppliersManagementReturn {
  suppliers: Supplier[]
  countrySites: CountrySite[]
  filters: SupplierFilters
  isLoading: boolean
  error: string
  lastPage: number
  total: number
  createSummary: string
  t: TFunction
  isOpen: boolean
  close: () => void
  setIsOpen: (open: boolean) => void
  searchInput: string
  setSearchInput: (search: string) => void
  updateFilters: (filters: Partial<SupplierFilters>) => void
  toggleSupplier: (supplier: Supplier) => void
  businessVerticalOf: (siteId: number) => string
}

const useSuppliersManagement = (): UseSuppliersManagementReturn => {
  const dispatch = useDispatch()
  const { extraRoutes } = useSelector(selectAuthData) as { extraRoutes?: { suppliers?: SuppliersRoutes } }
  const suppliersRoutes: SuppliersRoutes = extraRoutes?.suppliers ?? {}

  const { suppliers, filters, isLoading, error, lastPage, total } = useSelector(selectSuppliersManagement)
  const countrySites = useSelector(selectSuppliersManagementCountrySites)
  const createStatuses = useSelector(selectSuppliersManagementCreateStatuses)

  const { t } = useTranslation()
  const { isOpen, close, setIsOpen } = useModal()
  const [searchInput, setSearchInput] = useState(filters.search)

  const updateFilters = (updatedFilters: Partial<SupplierFilters>) => {
    dispatch(updateSuppliersManagementFilters(updatedFilters))
  }

  const toggleSupplier = (supplier: Supplier) => {
    dispatch({
      type: supplier.is_active
        ? suppliersManagementActions.DEACTIVATE_SUPPLIER
        : suppliersManagementActions.ACTIVATE_SUPPLIER,
      supplierId: supplier.id,
    })
  }

  const createSummary = Object.entries(createStatuses)
    .map(([type, status]) =>
      t(`suppliersManagement:create_status.${status}`, {
        type: t(`suppliersManagement:types.${type}`, type),
      }),
    )
    .join(' ')

  const businessVerticalOf = (siteId: number): string => {
    const site = countrySites.find(countrySite => countrySite.id === siteId)
    if (!site) return ''
    return t(`suppliersManagement:business_verticals.${site.business_type}`, site.business_type)
  }

  useEffect(() => {
    dispatch(updateSuppliersManagementCreateStatuses({}))
  }, [])

  useEffect(() => {
    if (searchInput === filters.search) return
    const handler = setTimeout(() => updateFilters({ search: searchInput }), 600)
    return () => clearTimeout(handler)
  }, [searchInput])

  useEffect(() => {
    if (!suppliersRoutes.list) return
    dispatch({ type: suppliersManagementActions.LOAD_LIST })
  }, [suppliersRoutes.list, filters])

  return {
    suppliers,
    countrySites,
    filters,
    isLoading,
    error,
    lastPage,
    total,
    createSummary,
    t,
    isOpen,
    close,
    setIsOpen,
    searchInput,
    setSearchInput,
    updateFilters,
    toggleSupplier,
    businessVerticalOf,
  }
}

export default useSuppliersManagement
